import { snakeCaseKeys } from './utils.js';
var AccessTokenErrorStatus;
(function (AccessTokenErrorStatus) {
    AccessTokenErrorStatus[AccessTokenErrorStatus["BadRequest"] = 400] = "BadRequest";
})(AccessTokenErrorStatus || (AccessTokenErrorStatus = {}));
class AccessTokenError extends Error {
    status;
    response;
    constructor(status, response) {
        super(['Authentication service error', response.error].filter(Boolean).join(': '));
        this.status = status;
        this.response = response;
        this.name = 'OAuth2Service.AccessTokenError';
    }
}
class ServiceFailureError extends Error {
    status;
    responseText;
    constructor(status, responseText) {
        super(`Authentication service failure: ${status}`);
        this.status = status;
        this.responseText = responseText;
        this.name = 'OAuth2Service.ServiceFailureError';
    }
    toString() {
        return (`Error: Authentication service failure\n` +
            `\tStatus: ${this.status}\n` +
            `\tResponse:\n\t${this.responseText}`);
    }
}
function isAccessTokenErrorStatus(status) {
    return OAuth2Service.wrongCredentialsStatusCodes.includes(status);
}
async function handleServiceError(res) {
    if (res.status >= 400 && !isAccessTokenErrorStatus(res.status)) {
        throw new ServiceFailureError(res.status, await res.text());
    }
}
export class OAuth2Service {
    configuration;
    /** The error status codes that the access token endpoint can normally respond with
     * when wrong credentials are provided. */
    static wrongCredentialsStatusCodes = Object.values(AccessTokenErrorStatus);
    /** Represents an error that the access token endpoint can normally respond with
     * when wrong credentials are provided, e.g. from a 400 response. */
    static AccessTokenError = AccessTokenError;
    /** Represents an unexpected service error, e.g. from a 500 response. */
    static ServiceError = ServiceFailureError;
    static async fetchAccessTokenApi(serviceUrl, grant) {
        const response = await fetch(`${serviceUrl}/oauth2/token`, {
            method: 'POST',
            body: new URLSearchParams(snakeCaseKeys(grant)).toString(),
            headers: {
                'content-type': 'application/x-www-form-urlencoded',
            },
        });
        await handleServiceError(response);
        const json = await response.json();
        if (isAccessTokenErrorStatus(response.status)) {
            return new AccessTokenError(response.status, json);
        }
        return json;
    }
    constructor(configuration) {
        this.configuration = configuration;
    }
    async callAccessTokenApi(grant) {
        switch (grant.grantType) {
            case 'authorization_code': {
                const { clientId, loginRedirectUri: redirectUri, serviceUrl } = this.configuration;
                return OAuth2Service.fetchAccessTokenApi(serviceUrl, { ...grant, clientId, redirectUri });
            }
            case 'refresh_token': {
                return OAuth2Service.fetchAccessTokenApi(this.configuration.serviceUrl, grant);
            }
        }
    }
    async getAuthorizeUri(_codeVerifier) {
        const codeVerifier = _codeVerifier || (await generateCodeVerifier());
        const { clientId, loginRedirectUri, serviceUrl } = this.configuration;
        const [codeChallengeMethod, codeChallenge] = await getCodeChallenge(codeVerifier);
        localStorage.setItem('codeVerifier', codeVerifier);
        return `${serviceUrl}/oauth2/authorize?${new URLSearchParams({
            client_id: clientId,
            code_challenge: codeChallenge,
            code_challenge_method: codeChallengeMethod,
            redirect_uri: loginRedirectUri,
            response_type: 'code',
        })}`;
    }
    getLogoutUri() {
        const { logoutRedirectUri, serviceUrl } = this.configuration;
        return `${serviceUrl}/logout?${new URLSearchParams({ redirect_uri: logoutRedirectUri })}`;
    }
    async authorize() {
        const iframeId = 'wolt-auth-authorize';
        const previousIframe = document.getElementById(iframeId);
        if (previousIframe) {
            previousIframe.remove();
        }
        const iframe = document.createElement('iframe');
        iframe.id = iframeId;
        iframe.hidden = true;
        iframe.title = 'Authorize';
        const authorizeUrl = await this.getAuthorizeUri();
        return new Promise((resolve, reject) => {
            const timeout = setTimeout(() => {
                cleanup();
                reject(new Error('Authorize loop timed out'));
            }, 5000);
            function cleanup() {
                removeEventListener('message', listener);
                iframe.remove();
                clearTimeout(timeout);
            }
            function listener(event) {
                if (event.origin === location.origin && event.data?.status) {
                    cleanup();
                    resolve(event.data.status);
                }
            }
            addEventListener('message', listener);
            document.body.append(iframe);
            iframe.src = authorizeUrl + '&prompt=none';
        });
    }
}
async function getCodeChallenge(codeVerifier) {
    const data = new TextEncoder().encode(codeVerifier);
    const hash = await crypto.subtle.digest('SHA-256', data);
    return ['S256', encodeBufferToURLSafeBase64(hash)];
}
export async function generateCodeVerifier() {
    const arr = new Uint8Array(32);
    crypto.getRandomValues(arr);
    return encodeBufferToURLSafeBase64(arr);
}
export function encodeBufferToURLSafeBase64(buffer) {
    return btoa(String.fromCharCode(...new Uint8Array(buffer)))
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=+$/, '');
}
/**
 * Performs OAuth2 login using the `login` method of the authenticator.
 * Call this when the OAuth2 login callback page loads.
 */
export async function oauth2Login({ login, authService, }) {
    const searchParams = new URLSearchParams(location.search);
    const code = searchParams.get('code');
    let codeVerifier = localStorage.getItem('codeVerifier');
    if (!code) {
        parent?.postMessage({ status: 'logged-out' });
        const errorCode = searchParams.get('error');
        throw new Error(`Missing auth code${errorCode ? `: ${errorCode}` : ''}`);
    }
    if (!codeVerifier) {
        // Missing code verifier indicates that the magic link was opened in another browser
        // - redirect to /authorize again
        codeVerifier = await generateCodeVerifier();
        localStorage.setItem('codeVerifier', codeVerifier);
        location.href = await authService.getAuthorizeUri(codeVerifier);
        throw new Error('Missing code verifier');
    }
    const isInIframe = !!window.parent;
    try {
        await login({ code, codeVerifier, grantType: 'authorization_code' }, { replace: isInIframe, lock: !isInIframe });
    }
    catch (error) {
        parent?.postMessage({ status: 'logged-out' });
        throw error;
    }
    localStorage.removeItem('codeVerifier');
    parent?.postMessage({ status: 'logged-in' });
}
/**
 * Performs OAuth2 logout using the `logout` method of the authenticator.
 * Call this when the OAuth2 logout callback page loads. */
export async function oauth2Logout({ logout }) {
    await logout();
}
